<script setup lang="ts">
import type { IAiChatDepositCalculation } from './types'
import { localizedField, formatRate, formatPeriodMonths } from '../utils'
import { EntityAiChatCalculationNote } from '../calculation-note'

const props = defineProps<{ items: readonly IAiChatDepositCalculation[] }>()
const { t, locale } = useI18n()

// Amounts go through useCurrencyFormat so separators, decimals and symbol
// placement follow each currency's own settings, as elsewhere in ai-chat.
const rows = computed(() =>
  props.items.map((item, index) => {
    const currencyId = useGetCurrencyIdByCode(item.currency)

    return {
      id: item.deposit_id,
      url: item.url,
      title: localizedField(item.title, locale.value),
      bank: localizedField(item.bank, locale.value),
      rate: formatRate(item.rate),
      period: formatPeriodMonths(item.period_months, t),
      income: useCurrencyFormat(item.income, currencyId),
      totalAmount: useCurrencyFormat(item.total_amount, currencyId),
      note: item.note,
      isLast: index === props.items.length - 1
    }
  })
)
</script>

<template>
  <div class="mt-2 overflow-x-auto [container-type:inline-size]">
    <table class="w-full min-w-fit border-separate border-spacing-0 text-xs">
      <thead class="bg-gray-100 text-left align-middle font-semibold">
        <tr>
          <th class="rounded-tl-lg border-l border-t border-gray-200 px-2 py-1.5">
            {{ $t('common.bank') }}
          </th>
          <th class="w-px border-l border-t border-gray-200 px-2 py-1.5 text-center">
            {{ $t('common.annual_rate') }}
          </th>
          <th class="w-px border-l border-t border-gray-200 px-2 py-1.5 text-center">
            {{ $t('entity.deposit.item.deposit_term') }}
          </th>
          <th class="w-px border-l border-t border-gray-200 px-2 py-1.5 text-right">
            {{ $t('common.income') }}
          </th>
          <th
            class="w-px rounded-tr-lg border-l border-r border-t border-gray-200 px-2 py-1.5 text-right"
          >
            {{ $t('common.total_amount') }}
          </th>
        </tr>
      </thead>
      <tbody class="align-middle">
        <template v-for="row in rows" :key="row.id">
          <tr
            class="group [&>td]:border-b [&>td]:border-l [&>td]:border-gray-200 [&>td]:px-2 [&>td]:py-1.5"
          >
            <td :class="row.isLast && !row.note ? 'rounded-bl-lg' : ''">
              <NuxtLink :to="row.url" class="block min-w-0 max-w-[9rem]">
                <div
                  class="line-clamp-2 font-semibold text-gray-900 group-hover:text-blue-600"
                  :title="row.title"
                >
                  {{ row.title }}
                </div>
                <div class="line-clamp-2 text-xs text-gray-500" :title="row.bank">
                  {{ row.bank }}
                </div>
              </NuxtLink>
            </td>
            <td class="whitespace-nowrap text-center">{{ row.rate }}</td>
            <td class="whitespace-nowrap text-center">{{ row.period ?? '—' }}</td>
            <td class="whitespace-nowrap text-right font-semibold">{{ row.income }}</td>
            <td
              :class="[
                'whitespace-nowrap border-r text-right font-semibold',
                row.isLast && !row.note ? 'rounded-br-lg' : ''
              ]"
            >
              {{ row.totalAmount }}
            </td>
          </tr>
          <EntityAiChatCalculationNote
            v-if="row.note"
            :note="row.note"
            :colspan="5"
            :is-last="row.isLast"
          />
        </template>
      </tbody>
    </table>
  </div>
</template>
